import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Avatar + AvatarGroup (design/components/avatar.html). Initials by default,
// photo when available, brand gradient for INHERTA staff. Presence + the staff
// gradient are conveyed in text too (never colour alone).

type AvatarSize = 32 | 36 | 42 | 54

const sizeMap: Record<AvatarSize, { box: string; text: string; dot: string }> = {
  32: { box: 'size-8', text: 'text-[12px]', dot: 'size-[9px]' },
  36: { box: 'size-9', text: 'text-[13px]', dot: 'size-2.5' },
  42: { box: 'size-[42px]', text: 'text-[15px]', dot: 'size-2.5' },
  54: { box: 'size-[54px]', text: 'text-[19px]', dot: 'size-3' },
}

const presenceDot = {
  online: 'bg-ok',
  away: 'bg-warn',
  offline: 'bg-text-3',
} as const

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

export interface AvatarProps {
  name: string
  src?: string | undefined
  size?: AvatarSize
  /** `staff` = INHERTA personnel (brand gradient); default `initials`. */
  variant?: 'initials' | 'staff'
  presence?: keyof typeof presenceDot
  className?: string
  /** Skip the sr-only name/presence text — for a table-row leading avatar
   *  where the name is already visible right next to it; without this a
   *  screen reader announces the name twice. */
  hideName?: boolean
}

/** Circular identity mark: photo when `src` is set, else initials derived from
 *  `name`; `variant: 'staff'` paints the INHERTA brand gradient. Name and
 *  presence are also exposed as sr-only text — never colour alone — unless
 *  `hideName` is set because the caller already shows the name visibly. */
export function Avatar({
  name,
  src,
  size = 36,
  variant = 'initials',
  presence,
  className,
  hideName,
}: AvatarProps) {
  const { t } = useTranslation('components')
  const s = sizeMap[size]
  // A stored photo can fail to load (a stale data URL, a decode error); when
  // it does, fall back to initials rather than showing a broken-image icon.
  const [failed, setFailed] = useState(false)
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      <span
        className={cn(
          'inline-flex items-center justify-center overflow-hidden rounded-full font-bold leading-none',
          s.box,
          s.text,
          variant === 'staff'
            ? 'bg-[image:var(--brand-gradient)] text-white'
            : 'bg-field text-text-2',
        )}
      >
        {src && !failed ? (
          <img
            src={src}
            alt=""
            className="size-full object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          <span aria-hidden>{initialsOf(name)}</span>
        )}
      </span>
      {!hideName && (
        <span className="sr-only">
          {name}
          {presence ? `, ${t(`ui.avatar.${presence}`)}` : ''}
        </span>
      )}
      {presence && (
        <span
          aria-hidden
          className={cn(
            'absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-surface',
            s.dot,
            presenceDot[presence],
          )}
        />
      )}
    </span>
  )
}

export interface AvatarGroupProps {
  people: AvatarProps[]
  /** How many to show before collapsing the rest into +N. */
  max?: number
  size?: 32 | 36
  className?: string
}

/** Overlapping row of Avatars, collapsing everyone past `max` into a +N
 *  bubble; forces a uniform small size on all members. */
export function AvatarGroup({ people, max = 4, size = 32, className }: AvatarGroupProps) {
  const shown = people.slice(0, max)
  const overflow = people.length - shown.length
  const moreBox = size === 32 ? 'size-8' : 'size-9'
  return (
    <span className={cn('inline-flex items-center', className)}>
      {shown.map((p, i) => (
        <span key={i} className={cn('rounded-full ring-2 ring-surface', i > 0 && '-ml-2')}>
          <Avatar {...p} size={size} />
        </span>
      ))}
      {overflow > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-field text-[11px] font-bold text-text-2 ring-2 ring-surface',
            moreBox,
            shown.length > 0 && '-ml-2',
          )}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}
